<template>
  <div>
    <div
      v-if="error"
      class="text-danger small mb-1"
    >
      {{ error }}
    </div>
    <iframe
      ref="frame"
      :key="doc"
      src="/sandbox.html"
      :style="{ height: height + 'px' }"
      class="w-100 border-0"
      title="cubus-ui"
      sandbox="allow-scripts allow-modals"
      referrerpolicy="no-referrer"
    />
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import { buildUiArtifactDoc, navigatePath, whereToFlat } from '@/cubus/html-sandbox/uiArtifactSdk'
import {
  queryObject, saveObjectRecord, deleteObjectRecord, runProcedure, objectFullOf, refOptionsOf,
} from '@/cubus/html-sandbox/htmlPageApi'

// CUB-150: предел запросов страницы — как у песочницы старых HTML-страниц (CbsHtmlSandbox):
// страница с запросом в цикле не должна забить сервер от имени пользователя.
const MAX_CONCURRENT = 20
const MAX_REQ_PER_SEC = 30

// CUB-144: интерфейс домена (артефакт `cbsui`, например страница записи) в песочнице legacy-фронта.
// Без allow-same-origin: у страницы нет ни нашего origin, ни токена, ни сети (CSP). Данные она просит
// у хоста, хост выполняет запрос обычным API — правами текущего пользователя.
export default {
  name: 'CbsUiArtifactFrame',
  props: {
    html: { type: String, required: true },
    params: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      height: 400, error: null, active: 0, times: [],
    }
  },
  computed: {
    doc() { return buildUiArtifactDoc(this.html) },
  },
  mounted() { window.addEventListener('message', this.onMessage) },
  beforeDestroy() { window.removeEventListener('message', this.onMessage) },
  methods: {
    post(msg) {
      const { frame } = this.$refs
      // targetOrigin '*': у песочницы opaque origin; отвечаем только ей и только на её запрос
      if (frame && frame.contentWindow) frame.contentWindow.postMessage({ __cubus: true, ...msg }, '*')
    },
    sendInit() {
      const session = this.$store.getters['cubus-store/SESSION']
      const u = session && session.user
      const user = u ? {
        sid: u.sid || null,
        name: u.name || null,
        roles: [u.role ? u.role.sid : null].filter(Boolean),
      } : null
      this.post({ type: 'init', user, params: this.params })
    },
    async onMessage(e) {
      const { frame } = this.$refs
      if (!frame || e.source !== frame.contentWindow) return
      const d = e.data
      // CUB-1418: песочница по пути со своей политикой CSP: `ready` — отдаём документ, `rendered` — страница нарисована, init
      // eslint-disable-next-line no-underscore-dangle
      if (d && d.__cubusSandbox === 'ready') { frame.contentWindow.postMessage({ __cubusSandbox: 'render', html: this.doc }, '*'); return }
      // eslint-disable-next-line no-underscore-dangle
      if (d && d.__cubusSandbox === 'rendered') { this.sendInit(); return }
      // имя поля `__cubus` задаёт протокол песочницы продукта
      // eslint-disable-next-line no-underscore-dangle
      if (!d || d.__cubus !== true) return
      if (d.type === 'height') {
        if (typeof d.height === 'number' && d.height > 40) this.height = Math.min(Math.ceil(d.height), 8000)
        return
      }
      if (d.type === 'error') { this.error = String(d.message || 'Ошибка в странице'); return }
      if (!d.id || !d.method) return
      const now = Date.now()
      this.times = this.times.filter(t => now - t < 1000)
      if (this.active >= MAX_CONCURRENT || this.times.length >= MAX_REQ_PER_SEC) {
        this.post({ id: d.id, ok: false, error: `Страница делает слишком много запросов (больше ${MAX_REQ_PER_SEC} в секунду или ${MAX_CONCURRENT} одновременно)` })
        return
      }
      this.times.push(now)
      this.active += 1
      try {
        this.post({ id: d.id, ok: true, result: await this.handle(d.method, d.args || {}) })
      } catch (err) {
        this.post({ id: d.id, ok: false, error: (err && err.message) || String(err) })
      } finally {
        this.active -= 1
      }
    },
    async handle(method, a) {
      switch (method) {
        case 'records': {
          const size = Math.min(Math.max(Number(a.size || a.pagesize) || 25, 1), 1000)
          const page = Math.max(Number(a.page) || 1, 1)
          const r = await queryObject({
            objectsid: a.objectsid, filter: whereToFlat(a.where), limit: size, offset: (page - 1) * size,
          })
          return { records: r.records, total: r.recordcount, columns: [] }
        }
        case 'record': {
          const r = await queryObject({ objectsid: a.objectsid, filter: { id: Number(a.id) }, limit: 1 })
          return (r.records && r.records[0]) || null
        }
        case 'save':
        case 'submit': {
          const values = { ...(a.values || {}) }
          if (method === 'submit') delete values.id
          return saveObjectRecord(a.objectsid, values)
        }
        case 'remove':
          return deleteObjectRecord(a.objectsid, { id: Number(a.id) })
        case 'objectFull':
          return objectFullOf(a.objectsid)
        case 'refOptions':
          return refOptionsOf(a.refid, a.q)
        case 'aggregate': {
          const filter = whereToFlat(a.where)
          if ((a.op || 'count') === 'count') {
            const r = await queryObject({ objectsid: a.objectsid, filter, limit: 1 })
            return { value: r.recordcount || 0 }
          }
          // сумма — по первым 1000 записям отбора; срез назван, а не выдан за полную сумму
          const r = await queryObject({ objectsid: a.objectsid, filter, limit: 1000 })
          const recs = r.records || []
          const value = recs.reduce((s, x) => s + (Number(x[a.field] && x[a.field].value) || 0), 0)
          return { value, counted: recs.length, truncated: (r.recordcount || 0) > recs.length }
        }
        case 'runProcedure':
          return runProcedure(a.sid, a.params || {})
        case 'navigate':
          // CUB-1353: {object, filter} → форма объекта с отбором; неверная цель — ошибка странице, а не молчание
          this.$router.push(navigatePath(a)).catch(() => {})
          return { ok: true }
        case 'toast':
          useCubus.toastMessage(this, String(a.text || ''))
          return { ok: true }
        default:
          throw new Error(`Метод '${method}' в этом интерфейсе недоступен`)
      }
    },
  },
}
</script>
